package com.rosario.juridico.marketplace.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.platform.FirebaseProxy
import com.rosario.juridico.marketplace.model.Propuesta
import com.rosario.juridico.marketplace.viewmodel.MarketplaceViewModel
import com.rosario.juridico.marketplace.viewmodel.UiState
import com.rosario.juridico.navigation.Routes

private val AzulCliente  = Color(0xFF1A7FD4)
private val VerdeAbogado = Color(0xFF1D9E75)

@Composable
fun DetalleConsultaScreen(consultaId: String, navController: NavController, viewModel: MarketplaceViewModel) {
    val currentUserId   = remember { FirebaseProxy.getCurrentUserId() }
    val feedConsultasState by viewModel.feedConsultasState.collectAsState()
    val propuestasState by viewModel.propuestasState.collectAsState()
    val abogadoInfo     by viewModel.currentUserAbogado.collectAsState()
    val isSubscribed    by viewModel.isUsuarioAbogadoSuscrito.collectAsState()
    val isLoggedIn      by viewModel.isLoggedIn.collectAsState()
    val isEmailVerified by viewModel.isEmailVerified.collectAsState()
    val enviarMensajeState by viewModel.enviarMensajeState.collectAsState()

    var showDialog      by remember { mutableStateOf(false) }

    val consulta = (feedConsultasState as? UiState.Success)?.data?.find { it.id == consultaId }

    val isOwner  = consulta?.userId == currentUserId
    
    // El contenido se bloquea si el usuario es abogado y NO está suscripto ni es premium, y NO es el dueño de la consulta
    val isRestricted = !isSubscribed && abogadoInfo != null && !isOwner

    LaunchedEffect(consultaId) { 
        viewModel.refreshAuthState()
        if (consultaId.isNotBlank()) {
            val inFeed = (feedConsultasState as? UiState.Success)?.data?.any { it.id == consultaId } == true
            if (!inFeed) {
                viewModel.loadConsultasAbiertas()
            }
            viewModel.loadPropuestas(consultaId) 
        }
    }

    Column(modifier = Modifier.fillMaxSize().imePadding()) {

        MktHeader(
            titulo    = "Detalle de Consulta",
            subtitulo = if (isRestricted) "Contenido Protegido" else "Consulta Legal",
            onBack    = { navController.popBackStack() }
        )

        // Verificación de email obligatoria
        if (!isLoggedIn) {
            Box(modifier = Modifier.fillMaxSize().padding(24.dp), contentAlignment = Alignment.Center) {
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(16.dp),
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f))
                ) {
                    Column(
                        modifier = Modifier.padding(24.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Icon(Icons.Default.AccountCircle, null, tint = AzulCliente, modifier = Modifier.size(48.dp))
                        Text(
                            "Iniciá Sesión",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold
                        )
                        Text(
                            "Debés iniciar sesión para ver los detalles de las consultas.",
                            textAlign = TextAlign.Center,
                            style = MaterialTheme.typography.bodyMedium
                        )
                        Button(
                            onClick = { navController.navigate(Routes.login(register = false)) },
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Text("Iniciar Sesión")
                        }
                    }
                }
            }
            return@Column
        }

        when {
            consultaId.isBlank() -> {
                Box(modifier = Modifier.weight(1f).fillMaxWidth().padding(24.dp), contentAlignment = Alignment.Center) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Icon(Icons.Default.ErrorOutline, null, modifier = Modifier.size(48.dp), tint = MaterialTheme.colorScheme.error)
                        Text("Identificador de consulta no válido", style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.error)
                        Button(onClick = { navController.popBackStack() }, shape = RoundedCornerShape(12.dp)) {
                            Text("Volver")
                        }
                    }
                }
            }
            feedConsultasState is UiState.Loading -> {
                Box(modifier = Modifier.weight(1f).fillMaxWidth(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = AzulCliente)
                }
            }
            consulta == null && feedConsultasState is UiState.Error -> {
                val errorMsg = (feedConsultasState as UiState.Error).message
                Box(modifier = Modifier.weight(1f).fillMaxWidth().padding(24.dp), contentAlignment = Alignment.Center) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Icon(Icons.Default.ErrorOutline, null, modifier = Modifier.size(48.dp), tint = MaterialTheme.colorScheme.error)
                        Text("Error al cargar la consulta: $errorMsg", style = MaterialTheme.typography.bodyMedium, textAlign = TextAlign.Center)
                        Button(
                            onClick = {
                                viewModel.loadConsultasAbiertas()
                                if (consultaId.isNotBlank()) viewModel.loadPropuestas(consultaId)
                            },
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Text("Reintentar")
                        }
                    }
                }
            }
            consulta == null -> {
                Box(modifier = Modifier.weight(1f).fillMaxWidth().padding(24.dp), contentAlignment = Alignment.Center) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Icon(Icons.Default.SearchOff, null, modifier = Modifier.size(48.dp), tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f))
                        Text("Consulta no encontrada", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                        Text("La consulta solicitada no existe o no se encuentra disponible.", style = MaterialTheme.typography.bodyMedium, textAlign = TextAlign.Center, color = MaterialTheme.colorScheme.onSurfaceVariant)
                        Button(onClick = { navController.popBackStack() }, shape = RoundedCornerShape(12.dp)) {
                            Text("Volver")
                        }
                    }
                }
            }
            else -> {
                Column(modifier = Modifier.weight(1f).fillMaxWidth()) {
                    LazyColumn(
                        modifier      = Modifier.weight(1f).fillMaxWidth(),
                        contentPadding = PaddingValues(16.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        item {
                            Card(
                                modifier  = Modifier.fillMaxWidth(),
                                shape     = RoundedCornerShape(20.dp),
                                colors    = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                                elevation = CardDefaults.cardElevation(2.dp)
                            ) {
                                Column(modifier = Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                    if (consulta.tipoConsulta == "colegiado") {
                                        Row(
                                            modifier = Modifier.fillMaxWidth(),
                                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                                        ) {
                                            if (!consulta.subtipoB2B.isNullOrBlank()) {
                                                Surface(shape = RoundedCornerShape(8.dp), color = Color(0xFF283593)) {
                                                    Text(
                                                        text = consulta.subtipoB2B!!.uppercase(),
                                                        modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                                                        style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold),
                                                        color = Color.White
                                                    )
                                                }
                                            }
                                            if (!consulta.jurisdiccionDestino.isNullOrBlank()) {
                                                Surface(shape = RoundedCornerShape(8.dp), color = Color(0xFF283593).copy(alpha = 0.1f)) {
                                                    Text(
                                                        text = "Destino: ${consulta.jurisdiccionDestino}",
                                                        modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                                                        style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold),
                                                        color = Color(0xFF283593)
                                                    )
                                                }
                                            }
                                            if (!consulta.modalidadHonorarios.isNullOrBlank()) {
                                                Surface(shape = RoundedCornerShape(8.dp), color = VerdeAbogado.copy(alpha = 0.1f)) {
                                                    Text(
                                                        text = "Honorarios: ${consulta.modalidadHonorarios}",
                                                        modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                                                        style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold),
                                                        color = VerdeAbogado
                                                    )
                                                }
                                            }
                                        }
                                    } else {
                                        Surface(shape = RoundedCornerShape(8.dp), color = AzulCliente.copy(alpha = 0.1f)) {
                                            Text(
                                                if (isRestricted) "Premium" else "Consulta Legal",
                                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 5.dp),
                                                style = MaterialTheme.typography.labelLarge.copy(fontWeight = FontWeight.Bold),
                                                color = AzulCliente
                                            )
                                        }
                                    }

                                    val desc = consulta.descripcion ?: ""
                                    val descVisible = if (isRestricted) {
                                        if (desc.length > 70) desc.take(70) + "..."
                                        else desc
                                    } else {
                                        desc
                                    }

                                    Text(descVisible, style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.onSurface)

                                    if (isRestricted) {
                                        Box(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f), RoundedCornerShape(12.dp))
                                                .padding(16.dp),
                                            contentAlignment = Alignment.Center
                                        ) {
                                            Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                                Icon(Icons.Default.Lock, null, tint = VerdeAbogado, modifier = Modifier.size(24.dp))
                                                Text(
                                                    "Suscribite para ver el contenido completo y datos de contacto.",
                                                    style = MaterialTheme.typography.bodySmall,
                                                    textAlign = TextAlign.Center,
                                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                                )
                                                Button(
                                                    onClick = { navController.navigate(Routes.REGISTRO_ABOGADO) },
                                                    colors = ButtonDefaults.buttonColors(containerColor = VerdeAbogado),
                                                    shape = RoundedCornerShape(8.dp)
                                                ) {
                                                    Text("Ver Suscripciones", fontSize = 12.sp)
                                                }
                                            }
                                        }
                                    } else {
                                        HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.15f))

                                        Row(horizontalArrangement = Arrangement.spacedBy(24.dp)) {
                                            val urg = consulta.urgencia ?: "Media"
                                            val est = consulta.estado ?: "pendiente"
                                            val ubi = consulta.ubicacion ?: "Rosario"
                                            MktInfoChip(Icons.Default.PriorityHigh, "Urgencia", urg,
                                                when (urg) {
                                                    "Alta"  -> Color(0xFFC62828)
                                                    "Media" -> Color(0xFFF57C00)
                                                    else    -> Color(0xFF2E7D32)
                                                }
                                            )
                                            MktInfoChip(Icons.Default.Circle, "Estado", est.uppercase(),
                                                if (est == "abierta") VerdeAbogado else AzulCliente
                                            )
                                            MktInfoChip(Icons.Default.LocationOn, "Zona", ubi, MaterialTheme.colorScheme.onSurfaceVariant)
                                        }
                                    }
                                }
                            }
                        }

                        if (isOwner) {
                            item {
                                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    Text("Propuestas recibidas", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold))
                                }
                            }
                            when (val s = propuestasState) {
                                is UiState.Loading -> item { Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) { CircularProgressIndicator(color = AzulCliente) } }
                                is UiState.Success -> {
                                    if (s.data.isEmpty()) {
                                        item {
                                            Card(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(16.dp), colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)) {
                                                Column(modifier = Modifier.padding(24.dp).fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                                    Icon(Icons.Default.HourglassEmpty, null, modifier = Modifier.size(40.dp), tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f))
                                                    Text("Esperando propuestas", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                                                }
                                            }
                                        }
                                    } else {
                                        items(s.data) { propuesta ->
                                            PropuestaCard(propuesta, isReadOnly = false) {
                                                viewModel.aceptarPropuesta(consultaId)
                                                navController.navigate("chat/$consultaId")
                                            }
                                        }
                                    }
                                }
                                else -> {}
                            }
                        }

                        item { Spacer(Modifier.height(16.dp)) }
                    }

                    // Botones de acción o chat inferior (Fijo en la parte inferior)
                    val esAbogado = abogadoInfo != null && !isOwner
                    if (esAbogado) {
                        if (isSubscribed) {
                            when ((consulta.estado ?: "pendiente").lowercase()) {
                                "abierta", "pendiente" -> {
                                    Surface(
                                        shadowElevation = 8.dp,
                                        color = MaterialTheme.colorScheme.surface
                                    ) {
                                        Column(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .padding(horizontal = 16.dp, vertical = 12.dp)
                                                .navigationBarsPadding(),
                                            verticalArrangement = Arrangement.spacedBy(10.dp)
                                        ) {
                                            var matricula by remember(abogadoInfo) { mutableStateOf(abogadoInfo?.matricula.orEmpty()) }
                                            var telefono by remember(abogadoInfo) { mutableStateOf(abogadoInfo?.telefono.orEmpty()) }
                                            var mensajePropuesta by remember { mutableStateOf("") }
                                            var intencionColega by remember { mutableStateOf("") }
                                            
                                            LaunchedEffect(enviarMensajeState) {
                                                if (enviarMensajeState is UiState.Success) {
                                                    mensajePropuesta = ""
                                                    matricula = ""
                                                    telefono = ""
                                                    intencionColega = ""
                                                    viewModel.resetEnviarMensajeState()
                                                }
                                            }

                                            if (consulta.tipoConsulta == "colegiado" && intencionColega.isEmpty()) {
                                                Row(
                                                    modifier = Modifier.fillMaxWidth(),
                                                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                                                ) {
                                                    OutlinedButton(
                                                        onClick = { intencionColega = "debate" },
                                                        modifier = Modifier.weight(1f).height(54.dp),
                                                        shape = RoundedCornerShape(12.dp)
                                                    ) {
                                                        Icon(Icons.Default.Forum, contentDescription = null, modifier = Modifier.size(18.dp))
                                                        Spacer(Modifier.width(8.dp))
                                                        Text("Debatir Caso", fontWeight = FontWeight.Bold)
                                                    }
                                                    Button(
                                                        onClick = { intencionColega = "derivacion" },
                                                        modifier = Modifier.weight(1f).height(54.dp),
                                                        colors = ButtonDefaults.buttonColors(containerColor = AzulCliente),
                                                        shape = RoundedCornerShape(12.dp)
                                                    ) {
                                                        Icon(Icons.Default.Handshake, contentDescription = null, modifier = Modifier.size(18.dp))
                                                        Spacer(Modifier.width(8.dp))
                                                        Text("Proponer Derivación", fontWeight = FontWeight.Bold)
                                                    }
                                                }
                                            } else {
                                                OutlinedTextField(
                                                    value = matricula,
                                                    onValueChange = { matricula = it },
                                                    placeholder = { Text("Matrícula Profesional") },
                                                    modifier = Modifier.fillMaxWidth(),
                                                    shape = RoundedCornerShape(12.dp),
                                                    singleLine = true,
                                                    enabled = enviarMensajeState !is UiState.Loading
                                                )

                                                OutlinedTextField(
                                                    value = telefono,
                                                    onValueChange = { telefono = it },
                                                    placeholder = { Text("Teléfono del Estudio") },
                                                    modifier = Modifier.fillMaxWidth(),
                                                    shape = RoundedCornerShape(12.dp),
                                                    singleLine = true,
                                                    keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(
                                                        keyboardType = androidx.compose.ui.text.input.KeyboardType.Number
                                                    ),
                                                    enabled = enviarMensajeState !is UiState.Loading
                                                )

                                                Row(
                                                    modifier = Modifier.fillMaxWidth(),
                                                    verticalAlignment = Alignment.Bottom,
                                                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                                                ) {
                                                    OutlinedTextField(
                                                        value = mensajePropuesta,
                                                        onValueChange = { mensajePropuesta = it },
                                                        placeholder = { 
                                                            Text(
                                                                when(intencionColega) {
                                                                    "debate" -> "Escribe tu aporte o debate sobre el caso..."
                                                                    "derivacion" -> "Propuesta para derivación del caso..."
                                                                    else -> "Propuesta comercial..."
                                                                }
                                                            ) 
                                                        },
                                                        modifier = Modifier.weight(1f),
                                                        shape = RoundedCornerShape(20.dp),
                                                        maxLines = 4,
                                                        enabled = enviarMensajeState !is UiState.Loading
                                                    )
                                                    val formValid = matricula.isNotBlank() && 
                                                                    telefono.isNotBlank() && 
                                                                    mensajePropuesta.isNotBlank()
                                                    
                                                    Button(
                                                        onClick = {
                                                            if (formValid) {
                                                                val intencion = intencionColega.ifEmpty { null }
                                                                viewModel.enviarMensaje(
                                                                    consultaId, 
                                                                    mensajePropuesta,
                                                                    matricula,
                                                                    telefono,
                                                                    intencion
                                                                )
                                                            }
                                                        },
                                                        modifier = Modifier.padding(bottom = 4.dp),
                                                        shape = RoundedCornerShape(20.dp),
                                                        colors = ButtonDefaults.buttonColors(containerColor = AzulCliente),
                                                        enabled = formValid && enviarMensajeState !is UiState.Loading
                                                    ) {
                                                        if (enviarMensajeState is UiState.Loading) {
                                                            CircularProgressIndicator(
                                                                modifier = Modifier.size(20.dp), 
                                                                color = Color.White, 
                                                                strokeWidth = 2.dp
                                                            )
                                                        } else {
                                                            Icon(Icons.Default.Send, contentDescription = "Enviar", modifier = Modifier.size(20.dp))
                                                            Spacer(Modifier.width(4.dp))
                                                            Text(if (intencionColega.isNotEmpty()) "Enviar" else "Enviar")
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                "en_proceso" -> {
                                    Box(modifier = Modifier.fillMaxWidth().padding(16.dp).navigationBarsPadding()) {
                                        Button(
                                            onClick  = { navController.navigate("chat/$consultaId") },
                                            modifier = Modifier.fillMaxWidth().height(54.dp),
                                            colors   = ButtonDefaults.buttonColors(containerColor = AzulCliente),
                                            shape    = RoundedCornerShape(16.dp)
                                        ) {
                                            Icon(Icons.Default.Chat, null, modifier = Modifier.size(18.dp))
                                            Spacer(Modifier.width(8.dp))
                                            Text("Ir al Chat", fontWeight = FontWeight.Bold)
                                        }
                                    }
                                }
                            }
                        } else {
                            // Banner de bloqueo persistente abajo
                            Box(modifier = Modifier.fillMaxWidth().padding(16.dp).navigationBarsPadding()) {
                                Card(
                                    modifier = Modifier.fillMaxWidth(),
                                    shape    = RoundedCornerShape(16.dp),
                                    colors   = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.2f))
                                ) {
                                    Column(modifier = Modifier.padding(16.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                                        Icon(Icons.Default.Lock, null, tint = MaterialTheme.colorScheme.error)
                                        Spacer(Modifier.height(8.dp))
                                        Text("Acceso Limitado", fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.error)
                                        Text(
                                            "Debés estar suscripto para contactar clientes y ver detalles de ubicación.",
                                            style = MaterialTheme.typography.bodySmall,
                                            textAlign = TextAlign.Center
                                        )
                                        Spacer(Modifier.height(12.dp))
                                        Button(
                                            onClick = { navController.navigate(Routes.REGISTRO_ABOGADO) },
                                            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error),
                                            shape = RoundedCornerShape(12.dp)
                                        ) {
                                            Text("Suscribirme ahora")
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    if (showDialog) {
        EnviarPropuestaDialog(
            onDismiss = { showDialog = false },
            onSend    = { mensaje, honorarios ->
                val monto = honorarios.toDoubleOrNull() ?: 0.0
                viewModel.enviarOfertaProfesional(consultaId, monto, mensaje)
                showDialog = false
            }
        )
    }
}

@Composable
fun MktInfoChip(icono: androidx.compose.ui.graphics.vector.ImageVector, label: String, valor: String, color: Color) {
    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(label, style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            Icon(icono, null, tint = color, modifier = Modifier.size(13.dp))
            Text(valor, style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.SemiBold), color = color)
        }
    }
}

@Composable
fun PropuestaCard(propuesta: Propuesta, isReadOnly: Boolean = false, onAceptar: () -> Unit) {
    Card(
        modifier  = Modifier.fillMaxWidth(),
        shape     = RoundedCornerShape(16.dp),
        colors    = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.cardElevation(2.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                Box(modifier = Modifier.size(40.dp), contentAlignment = Alignment.Center) {
                    Surface(shape = androidx.compose.foundation.shape.CircleShape, color = VerdeAbogado.copy(alpha = 0.12f)) {
                        Box(modifier = Modifier.size(40.dp), contentAlignment = Alignment.Center) {
                            Text(propuesta.abogadoNombre.take(2).uppercase(), style = MaterialTheme.typography.labelLarge.copy(fontWeight = FontWeight.Bold), color = VerdeAbogado)
                        }
                    }
                }
                Column(modifier = Modifier.weight(1f)) {
                    Text(propuesta.abogadoNombre, style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Bold), color = MaterialTheme.colorScheme.onSurface)
                    Text("Abogado verificado", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
                Surface(shape = RoundedCornerShape(8.dp), color = Color(0xFF2E7D32).copy(alpha = 0.1f)) {
                    Text("$${propuesta.honorarios}", modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp), style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold), color = Color(0xFF2E7D32))
                }
            }
            Text(propuesta.mensaje, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            if (!isReadOnly) {
                Button(
                    onClick  = onAceptar,
                    modifier = Modifier.fillMaxWidth(),
                    colors   = ButtonDefaults.buttonColors(containerColor = AzulCliente),
                    shape    = RoundedCornerShape(12.dp)
                ) {
                    Icon(Icons.Default.Chat, null, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(6.dp))
                    Text("Aceptar y Chatear", fontWeight = FontWeight.Bold)
                }
            }
        }
    }
}

@Composable
fun EnviarPropuestaDialog(onDismiss: () -> Unit, onSend: (String, String) -> Unit) {
    var mensaje    by remember { mutableStateOf("") }
    var honorarios by remember { mutableStateOf("") }

    AlertDialog(
        onDismissRequest = onDismiss,
        shape            = RoundedCornerShape(24.dp),
        icon             = { Icon(Icons.Default.Gavel, null, tint = VerdeAbogado) },
        title            = { Text("Nueva Propuesta", fontWeight = FontWeight.Bold) },
        text             = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(
                    value         = mensaje,
                    onValueChange = { mensaje = it },
                    label         = { Text("Mensaje para el cliente") },
                    modifier      = Modifier.fillMaxWidth(),
                    shape         = RoundedCornerShape(14.dp),
                    minLines      = 3
                )
                OutlinedTextField(
                    value         = honorarios,
                    onValueChange = { honorarios = it },
                    label         = { Text("Honorarios estimados ($)") },
                    modifier      = Modifier.fillMaxWidth(),
                    shape         = RoundedCornerShape(14.dp),
                    leadingIcon   = { Icon(Icons.Default.AttachMoney, null) },
                    keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(keyboardType = androidx.compose.ui.text.input.KeyboardType.Number)
                )
            }
        },
        confirmButton = {
            Button(onClick = { onSend(mensaje, honorarios) }, colors = ButtonDefaults.buttonColors(containerColor = VerdeAbogado), shape = RoundedCornerShape(12.dp)) {
                Text("Enviar", fontWeight = FontWeight.Bold)
            }
        },
        dismissButton = {
            TextButton(onClick = { onDismiss() }) { Text("Cancelar") }
        }
    )
}
